<template>
  <li
    class="pb-channel-item"
    :class="{
      active: props.active,
      'pb-channel-item-any-is-dragged': anyChannelIsDragged,
      'pb-channel-item-current-is-dragged': currentChannelIsDragged,
      'pb-channel-item-readonly': readonly,
    }"
  >
    <div
      v-if="!isGlobal"
      class="pb-channel-item-index"
    >
      {{ index }}
    </div>
    <!-- Channel Block -->
    <div class="pb-channel-item-content">
      <!-- Dropzone -->
      <div
        class="pb-channel-item-dropzone-wrapper"
      >
        <div
          ref="topDropzone"
          class="pb-channel-item-dropzone pb-channel-item-dropzone-top"
          :class="{ 'pb-channel-item-dropzone-active': topDropzoneActive }"
        />
        <div
          ref="bottomDropzone"
          class="pb-channel-item-dropzone pb-channel-item-dropzone-bottom"
          :class="{ 'pb-channel-item-dropzone-active': bottomDropzoneActive }"
        />
      </div>
      <!-- End Dropzone -->
      <!-- Actual Channel -->
      <LinkBox
        class="pb-channel-item-data"
        :to="href"
        @click.prevent="emit('navigate')"
      >
        <template #before>
          <div
            ref="reorderGrabber"
            class="pb-channel-item-reorder"
            :class="{ 'pb-channel-item-reorder-draggable': dragEnabled }"
          >
            <i class="icon-move-bar" />
          </div>
          <PbIcon
            class="pb-channel-item-icon"
            :value="icon"
          />
        </template>

        <div class="pb-channel-item-text-wrapper">
          <div class="pb-channel-item-text">
            {{ channel.linkName }}
            <PbItemDetails>
              {{ itemDetails }}
            </PbItemDetails>
          </div>
          <div class="pb-channel-item-icons-wrapper">
            <ReportIcon
              class="pb-channel-item-report"
              :report="channel.report"
            />
            <LinkIcon
              v-bind="channelData"
            />
          </div>
        </div>
      </LinkBox>
      <!-- End Actual Channel -->
    </div>
    <!-- End Channel Block -->
    <!-- Channel Dragging Follower -->
    <PbItem
      v-if="currentChannelIsDragged"
      class="pb-channel-item-drag-follower"
      :icon="icon"
      :style="dragFollowerStyle"
    >
      {{ channel.linkName }}
      <PbItemDetails>
        {{ itemDetails }}
      </PbItemDetails>
    </PbItem>
    <!-- End Channel Dragging Follower -->
  </li>
</template>

<script setup>
import {
  computed,
  ref,
  toRef,
  watch,
} from 'vue'
import useRootStore from '@/stores/root.store'
import { channelHelpers } from '@/helpers/profile'
import { useDrag, useDrop } from '@/composables/useDragDrop'
import { useReadonly } from '@/composables/useProfile'
import useIsGlobal from '@/composables/useIsGlobal'
import useChannelDetails from '@/composables/useChannelDetails'
import LinkBox from '@/components/LinkBox/LinkBox.vue'
import PbIcon from '@/components/PbIcon/PbIcon.vue'
import ReportIcon from '@/components/ItemIcons/ReportIcon.vue'
import PbItem from '@/components/PbItem/PbItem.vue'
import LinkIcon from '@/components/ItemIcons/LinkIcon.vue'
import PbItemDetails from '@/components/PbItemDetails/PbItemDetails.vue'

const { isGlobal } = useIsGlobal()

const rootStore = useRootStore()

const reorderGrabber = ref(null)
const topDropzone = ref(null)
const bottomDropzone = ref(null)

const emit = defineEmits([
  'navigate',
])

const props = defineProps({
  channel: { type: Object, default: () => ({}) },
  dragDropContext: { type: Object, default: () => ({}) },
  href: { type: String, default: '' },
  active: { type: Boolean, default: false },
})

const channel = toRef(props, 'channel')
const channelData = computed(() => channel.value.data)
const readonly = useReadonly()

const icon = computed(() => channelHelpers.getIcon(channelData.value))

const index = computed(() => (
  channelData.value._order + 1
  // String(channel.value.data._order + 1).padStart(3, '0')
))

const anyChannelIsDragged = computed(() => props.dragDropContext.isDragging.value)

const dragEnabled = computed(() => !readonly.value)

const { isDragging: currentChannelIsDragged, dragX, dragY } = useDrag(
  reorderGrabber,
  props.dragDropContext,
  channel,
  dragEnabled,
)

watch(currentChannelIsDragged, () => {
  // Add the "grabbed" cursor on everything if we're dragging
  rootStore.rootClasses['pb-dragging-active'] = currentChannelIsDragged
})

const dragFollowerStyle = computed(() => (
  { transform: `translateX(${dragX.value}px) translateY(-50%) translateY(${dragY.value}px)` }
))

const { active: topDropzoneActive } = useDrop(
  topDropzone,
  props.dragDropContext,
  {
    before: true,
    channel,
  },
)

const { active: bottomDropzoneActive } = useDrop(
  bottomDropzone,
  props.dragDropContext,
  {
    after: true,
    channel,
  },
)

const itemDetails = useChannelDetails(channelData)
</script>

<style lang="scss" src="./ChannelItem.scss" />
